import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Sprout, Droplets, Store, Leaf, Users, HeartHandshake, ArrowRight } from "lucide-react";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Reveal, Kicker, WfButton, ImageWithCredit } from "@/components/site/primitives";
import { ProgramHero } from "@/components/site/programs/ProgramHero";
import { FLATS } from "@/content/programs";

const OFFER_ICONS = [Sprout, Droplets, Store, Leaf, Users, HeartHandshake];

export default function FlatsMentorFarmPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <ProgramHero
          eyebrow={FLATS.hero.eyebrow}
          title={FLATS.hero.title}
          lede={FLATS.hero.lede}
          image={FLATS.hero.image}
          imageAlt={FLATS.hero.imageAlt}
          back={{ label: "All programs", href: "/programs" }}
        />

        {/* Intro */}
        <section id="flats-intro" aria-labelledby="flats-intro-heading" className="wf-section bg-[var(--wf-cream)]">
          <div className="wf-container">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <Reveal>
                <Kicker>{FLATS.intro.eyebrow}</Kicker>
                <h2 id="flats-intro-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-[2.6rem] lg:leading-[1.12]">
                  {FLATS.intro.title}
                </h2>
                <div className="mt-5 space-y-4">
                  {FLATS.intro.paragraphs.map((p, i) => (
                    <p key={i} className="text-base leading-7 text-[var(--wf-soil)]">{p}</p>
                  ))}
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <ImageWithCredit
                  src={FLATS.intro.image}
                  alt={FLATS.intro.imageAlt}
                  className="aspect-[4/5] w-full rounded-sm shadow-[var(--wf-shadow)]"
                  imgClassName="object-cover"
                />
              </Reveal>
            </div>
          </div>
        </section>

        {/* Stats band */}
        <section aria-label="Flats Mentor Farm at a glance" className="bg-[var(--wf-farm-green)] py-14 sm:py-16">
          <div className="wf-container">
            <div className="grid grid-cols-1 gap-8 text-center sm:grid-cols-3">
              {FLATS.stats.map((s, i) => (
                <Reveal key={s.id} delay={0.08 * i}>
                  <div data-testid={`flats-stat-${s.id}`}>
                    <p className="font-serif text-4xl font-semibold text-white sm:text-5xl">{s.value}</p>
                    <p className="mx-auto mt-2 max-w-[24ch] text-sm leading-6 text-[rgba(255,253,248,0.85)]">{s.label}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Offers */}
        <section id="flats-offers" aria-labelledby="flats-offers-heading" className="wf-section bg-white">
          <div className="wf-container">
            <Reveal className="mx-auto max-w-2xl text-center">
              <Kicker className="justify-center">{FLATS.offers.eyebrow}</Kicker>
              <h2 id="flats-offers-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">
                {FLATS.offers.title}
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {FLATS.offers.items.map((it, i) => {
                const Icon = OFFER_ICONS[i % OFFER_ICONS.length];
                return (
                  <Reveal key={it.title} delay={0.05 * i}>
                    <div data-testid={`flats-offer-${i}`} className="h-full rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-6">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[rgba(107,31,43,0.10)] text-[var(--wf-maroon)]">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <h3 className="mt-4 font-serif text-lg font-semibold text-[var(--wf-ink)]">{it.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-[var(--wf-soil)]">{it.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* History */}
        <section id="flats-history" aria-labelledby="flats-history-heading" className="wf-section bg-[var(--wf-cream)]">
          <div className="wf-container">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <Reveal delay={0.1} className="order-2 lg:order-1">
                <ImageWithCredit
                  src={FLATS.history.image}
                  alt={FLATS.history.imageAlt}
                  className="aspect-[5/4] w-full rounded-sm shadow-[var(--wf-shadow)]"
                  imgClassName="object-cover"
                />
              </Reveal>
              <Reveal className="order-1 lg:order-2">
                <Kicker>{FLATS.history.eyebrow}</Kicker>
                <h2 id="flats-history-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">
                  {FLATS.history.title}
                </h2>
                <p className="mt-4 text-base leading-7 text-[var(--wf-soil)]">{FLATS.history.body}</p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Cross-link to Mentorship */}
        <section aria-labelledby="flats-next-heading" className="bg-white py-16 sm:py-20">
          <div className="wf-container">
            <Reveal>
              <Link
                to="/programs/mentorship"
                data-testid="flats-to-mentorship-link"
                className="group flex flex-col items-start justify-between gap-6 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-wheat)] p-8 transition-[box-shadow,transform] duration-300 hover:shadow-[var(--wf-shadow)] motion-safe:hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background sm:flex-row sm:items-center sm:p-10"
              >
                <div>
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--wf-maroon)]">Next program</span>
                  <h2 id="flats-next-heading" className="mt-2 font-serif text-2xl font-semibold text-[var(--wf-ink)] sm:text-3xl">The Mentorship Program</h2>
                  <p className="mt-2 max-w-[52ch] text-sm leading-6 text-[var(--wf-soil)]">See the five-phase path that carries farmers from their first season to full independence.</p>
                </div>
                <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[var(--wf-maroon)] px-6 py-3 text-sm font-semibold text-white transition-colors group-hover:bg-[var(--wf-maroon-dark)]">
                  Explore mentorship
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
